import React, { useState, useRef, useEffect, useMemo } from "react";
import styles from "./PreviewV3.module.css";
import { CircularProgress } from "@mui/material";
import { notification } from "antd";
import { Strings } from "../../../../../../dataProvider/localize";
import { useDispatch } from "react-redux";
import { getSwmsFormData } from "../../../../../../actions/v2/JobSwmsActions";
import { getJobSwmsEmergencyPlans } from "../../../../../../actions/v2/JobSwmsEmergencyPlansActions";
import { getJobSwmsEmergencyProcedures } from "../../../../../../actions/v2/JobSwmsEmergencyProceduresActions";
import { getJobSwmsEmergencyReadiness } from "../../../../../../actions/v2/JobSwmsEmergencyReadinessActions";
import PreviewNav from "./PreviewNav";
import PreviewHeader from "./PreviewHeader";
import PreviewSections from "./PreviewSections";
import PreStartPreviewLayout from "./PreStartPreviewLayout";
import EmergencyReadinessPreviewLayout from "./emergency-readiness/EmergencyReadinessPreviewLayout";
import EmergencyPlansPreviewLayout from "./emergency-plans/EmergencyPlansPreviewLayout";
import EmergencyProceduresPreviewLayout from "./emergency-procedures/EmergencyProceduresPreviewLayout";
import { downloadDocument } from "../../../../../../utils/common";

const getDocumentTabs = (previewData) => {
  const document = (previewData && previewData.document) || previewData || {};
  const preStartChecklist = document.pre_start_checklist;
  const emergencyPlans = document.emergency_plans;
  const emergencyProcedures = document.emergency_procedures;
  const rescueReadinessQuestions = document.rescue_readiness_questions;

  return [
    { key: "swms", label: Strings.swms_txt, disabled: false },
    preStartChecklist && Number(preStartChecklist.toggle) !== 0 ? {
      key: "pre_start",
      label: "Pre-Start",
      disabled: false
    } : null,
    emergencyPlans && emergencyPlans.exists && emergencyPlans.toggle === 1 ? {
      key: "emergency_plans",
      label: "Emergency Plans",
      disabled: false
    } : null,
    emergencyProcedures && emergencyProcedures.exists && emergencyProcedures.toggle === 1 ? {
      key: "emergency_procedures",
      label: "Emergency Procedures",
      disabled: false
    } : null,
    rescueReadinessQuestions && rescueReadinessQuestions.exists && rescueReadinessQuestions.toggle === 1 ? {
      key: "emergency_readiness",
      label: "Emergency Readiness",
      disabled: false
    } : null
  ].filter(Boolean);
};

const getPdfStatusText = (pdfStatus) => {
  switch (pdfStatus) {
    case "not_generated":
      return "SWMS PDF not generated yet";
    case "in_generation":
      return "SWMS PDF is being generated";
    case "generated":
      return "Download SWMS PDF";
    case "errored":
      return "Failed to generate PDF for this SWMS.";
    default:
      return "";
  }
};

export default function PreviewV3Container({ previewJobId, swms, onClose }) {

  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [previewData, setPreviewData] = useState(null);
  const [pdfLink, setPdfLink] = useState(null);
  const [emergencyPlansPdfLink, setEmergencyPlansPdfLink] = useState(null);
  const [emergencyProceduresPdfLink, setEmergencyProceduresPdfLink] = useState(null);
  const [approvalStatus, setApprovalStatus] = useState(null);
  const [pdfStatus, setPdfStatus] = useState("not_generated");
  const [activeDocumentTab, setActiveDocumentTab] = useState("swms");
  const previewScrollRef = useRef(null);
  const DOCUMENT_TABS = useMemo(() => getDocumentTabs(previewData), [previewData]);
  const swmsNumber = useMemo(() => {
    const previewSwmsNumber = previewData && previewData.sections && previewData.sections.document_project_details && previewData.sections.document_project_details.data
      ? previewData.sections.document_project_details.data.swms_number
      : null;
    const fallbackSwmsNumber = swms && swms.swms_number;
    const value = previewSwmsNumber || fallbackSwmsNumber;

    return value === null || value === undefined || String(value).trim() === ""
      ? null
      : String(value).trim();
  }, [previewData, swms]);

  useEffect(() => {
    const activeTab = DOCUMENT_TABS.find((tab) => tab.key === activeDocumentTab);
    if (!activeTab || activeTab.disabled) {
      setActiveDocumentTab("swms");
    }
  }, [DOCUMENT_TABS, activeDocumentTab]);

  useEffect(() => {
    if (previewScrollRef.current) {
      previewScrollRef.current.scrollTo({ top: 0, behavior: "auto" });
    }
  }, [activeDocumentTab]);

  const controller = useRef();
  const emergencyPreviewData = useRef({
    plans: null,
    procedures: null,
    readiness: null
  });

  const getPreviewDetails = () => {
    setLoading(true);
    controller.current = new AbortController();
    const requestConfig = { signal: controller.current.signal };
    const swmsId = swms.swms_form_id;

    Promise.all([
      dispatch(getSwmsFormData(requestConfig, swmsId, previewJobId)),
      dispatch(getJobSwmsEmergencyPlans(requestConfig, swmsId, previewJobId)),
      dispatch(getJobSwmsEmergencyProcedures(requestConfig, swmsId, previewJobId)),
      dispatch(getJobSwmsEmergencyReadiness(requestConfig, swmsId, previewJobId))
    ]).then(([swmsResponse, plansResponse, proceduresResponse, readinessResponse]) => {
      const allInfo = swmsResponse.swms_data;
      setPreviewData(allInfo);
      setApprovalStatus(swmsResponse.status);
      setPdfLink(swmsResponse.pdfLink);
      setPdfStatus(swmsResponse.pdfStatus);
      setEmergencyPlansPdfLink(swmsResponse.emergencyPlansPdf && swmsResponse.emergencyPlansPdf.pdfLink);
      setEmergencyProceduresPdfLink(swmsResponse.emergencyProceduresPdf && swmsResponse.emergencyProceduresPdf.pdfLink);
      emergencyPreviewData.current = {
        plans: plansResponse,
        procedures: proceduresResponse,
        readiness: readinessResponse
      };
      setLoading(false);
    }).catch(err => {
      const requestWasAborted = controller.current && controller.current.signal.aborted;
      const abortError = requestWasAborted || (err && (err.name === "AbortError" || err.code === "ERR_CANCELED" || err.message === "canceled"));

      if (!abortError) {
        setLoading(false);
        notification.error({
          message: Strings.error_title,
          description: err ? err : Strings.generic_error,
          className: "ant-error"
        });
        onClose();
      }
    });
  }

  useEffect(() => {
    setActiveDocumentTab("swms");
  }, [previewJobId]);

  useEffect(() => {
    getPreviewDetails();
    return () => {
      if (controller.current) {
        controller.current.abort();
      }
    }
  }, [])

  const onJsaActivitiesChange = (activities) => {
    setPreviewData((prev) => {
      if (!prev || !prev.sections || !prev.sections.job_steps_hazards_consequences_controls) {
        return prev;
      }

      return {
        ...prev,
        sections: {
          ...prev.sections,
          job_steps_hazards_consequences_controls: {
            ...prev.sections.job_steps_hazards_consequences_controls,
            data: {
              ...prev.sections.job_steps_hazards_consequences_controls.data,
              toggle: 1,
              activities,
            }
          }
        }
      };
    });
  };
  const isPdfDownloadTab = ["swms", "emergency_plans", "emergency_procedures"].includes(activeDocumentTab);
  const activePdfLink = activeDocumentTab === "emergency_plans"
    ? emergencyPlansPdfLink
    : (activeDocumentTab === "emergency_procedures" ? emergencyProceduresPdfLink : pdfLink);
  const activePdfStatus = activeDocumentTab === "swms" ? pdfStatus : "generated";

  return (
    <div className={styles.PreviewOverlay}>
      <div className={styles.PreviewModal}>
        <div ref={previewScrollRef} className={styles.viewPreview}>
          <div className={styles.PreviewPlatformBar}>
            <div>
              <button className={styles.PreviewPlatformButton} onClick={onClose}>
                <svg height="16px" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">
                  <path fill="currentColor" d="M73.4 297.4C60.9 309.9 60.9 330.2 73.4 342.7L233.4 502.7C245.9 515.2 266.2 515.2 278.7 502.7C291.2 490.2 291.2 469.9 278.7 457.4L173.3 352L544 352C561.7 352 576 337.7 576 320C576 302.3 561.7 288 544 288L173.3 288L278.7 182.6C291.2 170.1 291.2 149.8 278.7 137.3C266.2 124.8 245.9 124.8 233.4 137.3L73.4 297.3z" /></svg>
                <span>Back</span>
              </button>
            </div>
            <div className={styles.PreviewPlatformBarRight}>
              {!loading ? (
                <div title={getPdfStatusText(pdfStatus)}>
                  {pdfStatus === "in_generation" && (
                    <CircularProgress sx={{ color: "#fff" }} size={14} style={{ marginRight: "10px" }} />
                  )}
                  {isPdfDownloadTab ? (
                  <button onClick={() => downloadDocument(activePdfLink)} disabled={!activePdfLink || activePdfStatus !== "generated"} className={styles.PreviewPlatformButton} style={{ background: "#6366F1", color: "#fff", borderColor: "#6366F1" }}>
                    <svg height="16px" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640">
                      <path fill="currentColor" d="M128 128C128 92.7 156.7 64 192 64L341.5 64C358.5 64 374.8 70.7 386.8 82.7L493.3 189.3C505.3 201.3 512 217.6 512 234.6L512 512C512 547.3 483.3 576 448 576L192 576C156.7 576 128 547.3 128 512L128 128zM336 122.5L336 216C336 229.3 346.7 240 360 240L453.5 240L336 122.5zM303 505C312.4 514.4 327.6 514.4 336.9 505L400.9 441C410.3 431.6 410.3 416.4 400.9 407.1C391.5 397.8 376.3 397.7 367 407.1L344 430.1L344 344C344 330.7 333.3 320 320 320C306.7 320 296 330.7 296 344L296 430.1L273 407.1C263.6 397.7 248.4 397.7 239.1 407.1C229.8 416.5 229.7 431.7 239.1 441L303.1 505z" />
                    </svg>
                    <span>Download</span>
                  </button>
                  ) : null}
                  {pdfStatus === "errored" && (
                    <span style={{ color: "red", marginRight: "10px", verticalAlign: "middle" }}>
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" height="20px" width="20px">
                        <path fill="#f44336" d="M128 64C92.7 64 64 92.7 64 128L64 512C64 547.3 92.7 576 128 576L308 576C285.3 544.5 272 505.8 272 464C272 363.4 349.4 280.8 448 272.7L448 234.6C448 217.6 441.3 201.3 429.3 189.3L322.7 82.7C310.7 70.7 294.5 64 277.5 64L128 64zM389.5 240L296 240C282.7 240 272 229.3 272 216L272 122.5L389.5 240zM464 608C543.5 608 608 543.5 608 464C608 384.5 543.5 320 464 320C384.5 320 320 384.5 320 464C320 543.5 384.5 608 464 608zM464 508C475 508 484 517 484 528C484 539 475 548 464 548C453 548 444 539 444 528C444 517 453 508 464 508zM464 368C472.8 368 480 375.2 480 384L480 464C480 472.8 472.8 480 464 480C455.2 480 448 472.8 448 464L448 384C448 375.2 455.2 368 464 368z" />
                      </svg>
                    </span>
                  )}
                </div>
              ) : null}
            </div>
          </div>
          {!loading && previewData && previewData.meta && previewData.meta.template_version === 3 && (
            <nav className={styles.PreviewDocumentTabs} aria-label="SWMS document tabs" role="tablist">
              {DOCUMENT_TABS.map((tab, index) => (
                <button
                  key={tab.key}
                  type="button"
                  className={`${styles.PreviewDocumentTab} ${activeDocumentTab === tab.key ? styles.PreviewDocumentTabActive : ""}`}
                  onClick={() => {
                    if (!tab.disabled) {
                      setActiveDocumentTab(tab.key);
                    }
                  }}
                  aria-selected={activeDocumentTab === tab.key}
                  aria-disabled={tab.disabled}
                  disabled={tab.disabled}
                  title={tab.disabled ? tab.title : undefined}
                  role="tab"
                >
                  <span className={styles.PreviewDocumentTabNumber}>{index + 1}</span>
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          )}
          {loading ? (
            <div className="d-flex align-items-center justify-content-center" style={{ height: "calc(100% - 54px)" }}>
              <CircularProgress size={30} />
            </div>
          ) : previewData && previewData.meta && previewData.meta.template_version && previewData.meta.template_version === 3 ? (
            activeDocumentTab === "swms" ? (
              <div className={`${styles.PreviewWrap} ${styles.SwmsPreviewDocument}`}>
                <PreviewNav swmsData={previewData} />
                <main>
                  <div className={styles.PreviewDoc}>
                    <PreviewHeader swmsData={previewData} swms={swms} />
                    <PreviewSections swmsData={previewData} approvalStatus={approvalStatus} jobId={previewJobId} swmsId={swms && swms.swms_form_id ? swms.swms_form_id : null} onJsaActivitiesChange={onJsaActivitiesChange} />
                  </div>
                </main>
              </div>
            ) : activeDocumentTab === "pre_start" ? (
              <PreStartPreviewLayout
                swmsNumber={swmsNumber}
                sections={previewData.sections || {}}
                preStartChecklist={(previewData.document && previewData.document.pre_start_checklist) || previewData.pre_start_checklist}
                approvalStatus={approvalStatus}
              />
            ) : activeDocumentTab === "emergency_plans" ? (
              <EmergencyPlansPreviewLayout
                plans={emergencyPreviewData.current.plans}
                sections={previewData.sections || {}}
                context={{ jobId: previewJobId, swmsId: swms && swms.swms_form_id, approvalStatus }}
              />
            ) : activeDocumentTab === "emergency_procedures" ? (
              <EmergencyProceduresPreviewLayout
                procedures={emergencyPreviewData.current.procedures}
                sections={previewData.sections || {}}
                context={{ jobId: previewJobId, swmsId: swms && swms.swms_form_id, approvalStatus }}
                pdfLink={pdfLink}
                pdfStatus={pdfStatus}
              />
            ) : activeDocumentTab === "emergency_readiness" ? (
              <EmergencyReadinessPreviewLayout
                swmsNumber={swmsNumber}
                sections={previewData.sections || {}}
                readiness={emergencyPreviewData.current.readiness}
              />
            ) : (
              <div className={styles.PreviewDocumentTabBody} />
            )
          ) : (
            <div className="d-flex align-items-center justify-content-center" style={{ height: "calc(100% - 54px)" }}>
              <div style={{ textAlign: "center", color: "red" }}>
                <p>Unsupported SWMS Document.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div >
  )
}
